import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import Navbar from '../../components/navbar/navbar'
import ListProject from '../../components/list-project'
import DetailSidebarThree from '../../components/detail-sidebar-three'
import Footer from '../../components/footer'

import userImg from '../../assets/img/user-5.jpg'
import { API_BASE, getImageUrl } from '../../utils/api'

interface DBProperty {
    _id: string; title: string; price: number; status: string; type: string;
    city?: string; address?: string; bedrooms?: number; bathrooms?: number; area?: string;
    images?: string[]; isActive: boolean; isFeatured?: boolean; createdAt: string;
    owner?: { name: string; role: string };
}

interface DBAgent {
    _id: string; name: string; email: string; role: string; phone?: string;
    about?: string; city?: string; state?: string; avatar?: string;
    facebook?: string; twitter?: string; googleplus?: string; linkedin?: string;
}

export default function AgentPage() {
    const [agent, setAgent] = useState<DBAgent | null>(null)
    const [properties, setProperties] = useState<DBProperty[]>([])
    const [projects, setProjects] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    const params = useParams()
    const id = params.id

    useEffect(() => {
        if (!id) return;
        setLoading(true)
        fetch(`${API_BASE}/users/agents/${id}`)
            .then(r => r.json())
            .then(data => {
                if (data.agent) {
                    setAgent(data.agent)
                    setProperties(data.properties || [])
                    setProjects(data.projects || [])
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [id])

    const handleCompare = (project: any) => {
        try {
            const existing = localStorage.getItem('compareList');
            let list = existing ? JSON.parse(existing) : [];
            if (!list.find((p: any) => p._id === project._id)) {
                if (list.length >= 3) {
                    alert('You can compare up to 3 properties at a time.');
                } else {
                    list.push(project);
                    localStorage.setItem('compareList', JSON.stringify(list));
                    window.dispatchEvent(new Event('compareUpdated'));
                }
            }
        } catch (error) {
            console.error('Error adding to compare:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-6 text-slate-500 font-black uppercase tracking-widest text-[10px]">Retrieving Agent Profile...</p>
            </div>
        )
    }

    if (!agent) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-10 text-center">
                <div className="w-24 h-24 bg-rose-50 dark:bg-rose-900/20 rounded-full flex items-center justify-center mb-8">
                    <i className="fa-solid fa-user-slash text-4xl text-rose-500"></i>
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2">Agent Not Found</h3>
                <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-sm">The agent profile you are seeking is either disconnected or no longer part of our ecosystem.</p>
                <Link to="/agents" className="px-10 py-4 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black uppercase tracking-widest text-[10px] hover:scale-105 active:scale-95 transition-all shadow-xl shadow-slate-900/10">Return to Agency House</Link>
            </div>
        )
    }

    return (
        <div className="bg-slate-50/50 dark:bg-slate-950 min-h-screen text-slate-800 dark:text-slate-100 transition-colors duration-300">
            <Navbar transparent={false} />

            {/* Simple Light Breadcrumbs Header */}
            <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200/40 dark:border-slate-800/80 py-6">
                <div className="container mx-auto px-6">
                    <div className="flex items-center gap-2">
                        <Link to="/agents" className="text-[10px] font-black text-slate-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400 uppercase tracking-widest transition-colors">Agents</Link>
                        <i className="fa-solid fa-chevron-right text-[8px] text-slate-300 dark:text-slate-700"></i>
                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{agent.name}</span>
                    </div>
                </div>
            </div>

            {/* Overlapping Main Content */}
            <section className="py-12 relative z-20">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        
                        {/* Left Column: Profile info & portfolio */}
                        <div className="lg:col-span-8 space-y-8">
                            
                            {/* Sleek Profile Info Card */}
                            <div className="bg-white dark:bg-slate-900/90 rounded-3xl border border-slate-155 dark:border-slate-800/80 shadow-md p-6 md:p-8">
                                <div className="flex flex-col md:flex-row gap-8 items-center text-center md:text-left">
                                    
                                    {/* Avatar circular frame */}
                                    <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl overflow-hidden ring-4 ring-slate-100/50 dark:ring-slate-855 shadow-md shrink-0 transition-transform duration-500 hover:scale-105 relative">
                                        <img 
                                            src={agent.avatar ? getImageUrl(agent.avatar) : userImg} 
                                            alt={agent.name} 
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    
                                    {/* Profile Meta & Details */}
                                    <div className="flex-grow flex flex-col justify-center">
                                        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-955 dark:text-white tracking-tight">{agent.name}</h2>
                                        <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">
                                            {agent.role === 'broker' ? 'Elite Broker' : 'Certified Agent'}
                                        </span>
                                    </div>
                                    
                                </div>
                            </div>

                            {/* Portfolio Listings Section */}
                            <div className="space-y-6">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-155 dark:border-slate-800/80 pb-4">
                                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                                        <span className="w-1 h-5 bg-blue-600 rounded-full"></span> Submitted Projects ({projects.length})
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 gap-6">
                                    {projects.length > 0 ? (
                                        projects.map((item) => (
                                            <ListProject key={item._id} item={item as any} handleCompare={handleCompare} />
                                        ))
                                    ) : (
                                        <div className="col-span-full py-16 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-850 text-center">
                                            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800/40 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-355 dark:text-slate-700">
                                                <i className="fa-solid fa-building-circle-exclamation text-2xl"></i>
                                            </div>
                                            <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-1 uppercase tracking-wider">Zero Projects</h4>
                                            <p className="text-xs text-slate-400">No projects submitted by this broker yet.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Interaction Sidebar */}
                        <div className="lg:col-span-4">
                            <DetailSidebarThree agent={agent} />
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}
